import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import * as ProcessSchemasDAO from 'processes/dao/ProcessSchemaDAO';
import isSchemaExecutionTemplateUnpublished from 'processes/settings/isSchemaExecutionTemplateUnpublished';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ORANGE_DARK, ORANGE_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {GetUnpublishedSchemasCountQuery} from './__generated/GetUnpublishedSchemasCountQuery';
import EmptySidebarIcon from './EmptySidebarIcon';
import {Section, SettingsSection} from './SidebarConstants';
import SidebarItem from './SidebarItem';

interface ProcessSchemaSettingsProps {
  section: SettingsSection;
}

export const GET_UNPUBLISHED_SCHEMAS_COUNT = gql`
  query GetUnpublishedSchemasCountQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: SCHEMA_CONFIG) {
      id
      name
      archiveRecord {
        purpose
      }
      entryTemplate {
        id
        currentPublishingRecord {
          ... on PublishingRecordNode {
            id
            publishingStatus
          }
          ...InaccessibleInfoGraphQL
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcessSchemaSettings(props: ProcessSchemaSettingsProps): JSX.Element {
  const response = useBenchlingQuery<GetUnpublishedSchemasCountQuery>(GET_UNPUBLISHED_SCHEMAS_COUNT);
  // We don't show any kind of loading state here because the badge only appears in edge cases and adding an
  // explicit loading state may add additional confusion. This also matches other setting sidebar items like
  // registry settings which shows a similar badge.
  const unpublishedSchemasCount =
    response.type === QueryResultType.SUCCESS
      ? response.data.processTaskSchemasSupportingPlatformFeature.filter(
          (schema) => !isArchived(schema) && isSchemaExecutionTemplateUnpublished(schema)
        ).length
      : 0;

  return (
    <div>
      <div className="browser-nav-header">Workflow Schemas</div>
      <SidebarItem
        active={props.section === Section.PROCESS_SCHEMAS}
        icon={<EmptySidebarIcon />}
        name="Task schemas"
        badge={
          unpublishedSchemasCount > 0 ? (
            <div
              className={css(styles.badge)}
              {...getTestProps({component: 'ProcessSchemaSettings', element: 'unpublishedSchemasBadge'})}
            >
              <BenchlingIcon icon="warning" />
              <div className={css(styles.badgeText)}>{unpublishedSchemasCount}</div>
            </div>
          ) : null
        }
        link={ProcessSchemasDAO.processTaskSchemasURL()}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  badge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: ORANGE_DARK,
    backgroundColor: ORANGE_LIGHTER,
    borderRadius: 2,
    paddingLeft: 4,
    paddingRight: 4,
  },
  badgeText: {
    marginLeft: 8,
  },
});
